import { FC, useMemo } from 'react';
import toast from 'react-hot-toast';
import { ProviderType } from 'videoservice-openapi-react';
import { useUpdateBroadcast } from 'features/broadcast/update-broadcast';
import { useUpdateBroadcastCustomerRestrictions } from 'entities/broadcast-customer-restrictions';
import { useModalContext } from 'shared/lib/modal';
import { EColorsNames } from 'shared/types';
import { Badge, DoneIcon } from 'shared/ui';
import { ModalContent } from 'shared/ui/modal-content';
import { useManualBroadcastState } from '../../lib';
import { ManualBroadcast } from '../manual-broadcast/manual-broadcast';
import { ManualBroadcastModalFooter } from '../manual-broadcast-modal-footer';
import { BroadcastModalHeaderStyled } from './edit-broadcast-modal-content.styled';
import {
  IBroadcastModalHeaderProps,
  IEditBroadcastModalContentProps,
} from './edit-broadcast-modal-content.types';

const BroadcastModalHeader: FC<IBroadcastModalHeaderProps> = ({
  broadcastId,
  broadcastProviderType,
}) => {
  const badgeText = useMemo(() => {
    if (broadcastProviderType === ProviderType.MANUAL) {
      return 'Добавлена в ручную';
    } else {
      return 'Добавлена автоматически';
    }
  }, [broadcastProviderType]);

  const badgeColor = useMemo(() => {
    if (broadcastProviderType === ProviderType.MANUAL) {
      return EColorsNames.Yellow;
    } else {
      return EColorsNames.Blue;
    }
  }, [broadcastProviderType]);

  return (
    <BroadcastModalHeaderStyled>
      <span>ID{broadcastId}</span>
      <Badge color={badgeColor} isBordered>
        {badgeText}
      </Badge>
    </BroadcastModalHeaderStyled>
  );
};

export const EditBroadcastModalContent: FC<IEditBroadcastModalContentProps> = ({
  broadcast,
}) => {
  const { closeModal } = useModalContext();
  const { changeHandler, newBroadcastData, newBroadcastRestrictionsData } =
    useManualBroadcastState();
  const { updateBroadcastMutation } = useUpdateBroadcast(broadcast.id);
  const { updateBroadcastCustomerRestrictionsMutation } =
    useUpdateBroadcastCustomerRestrictions();

  const canProceed = useMemo(
    () =>
      Boolean(
        newBroadcastData.sportId &&
          newBroadcastData.startDate &&
          newBroadcastData.streamType &&
          newBroadcastData.title,
      ),
    [newBroadcastData],
  );

  const onSubmit = () => {
    updateBroadcastMutation.mutate(newBroadcastData, {
      onSuccess() {
        updateBroadcastCustomerRestrictionsMutation.mutate(
          {
            broadcastId: broadcast.id,
            restrictions: newBroadcastRestrictionsData,
          },
          {
            onSuccess() {
              toast(`Трансляция успешно обновлена`, {
                icon: <DoneIcon />,
              });
              closeModal();
            },
          },
        );
      },
    });
  };

  return (
    <ModalContent
      $title={
        <BroadcastModalHeader
          broadcastId={broadcast.id}
          broadcastProviderType={broadcast.providerType}
        />
      }
      footer={
        <ManualBroadcastModalFooter
          isLoading={
            updateBroadcastMutation.isPending ||
            updateBroadcastCustomerRestrictionsMutation.isPending
          }
          onSubmit={onSubmit}
          canProceed={canProceed}
        />
      }
    >
      <ManualBroadcast broadcast={broadcast} onChange={changeHandler} />
    </ModalContent>
  );
};
